/**
 * Copyright (c) 2022, ResMed Inc. All rights reserved.
 */

/**
 * DynamoDBService
 *
 * Service for Werewolf API DynamoDB calls
 */

export default class DynamoDBService {
    _AWSClient;
    _HttpService;

    baseUrl = `${process.env.REACT_APP_WEREWOLF_API_INVOKE_URL}/v1/dynamodb`

    constructor(httpService) {
        this._AWSClient = require('aws-sdk');
        this._HttpService = httpService;
    }

    /**
     * Async function to scan items that contain the sourceId given
     *
     * @param sourceId sourceId to contain
     * @param sortKey  filter/sort key
     * @param startKey where to start scan (used for paging, optional)
     * @return data
     */
    async scanContainsSourceId(sourceId, sortKey, startKey) {
        if (sourceId == '#'){
            return {};
        }

        let body = `{"sourceId": "${sourceId}","sortKey": "${sortKey}"}`;

        if (startKey) {
            const lastSourceId = startKey.sourceId;
            const lastSortKey = startKey.sortKey;

            if (lastSourceId && lastSourceId.S && lastSortKey && lastSortKey.S) {
                body = `{"sourceId": "${sourceId}","sortKey": "${sortKey}", "lastSourceId": "${lastSourceId.S}", "lastSortKey": "${lastSortKey.S}"}`;
            }
        }

        let response_data = await this._HttpService.async_result(`${this.baseUrl}/scan`, body, `POST`)
        response_data["Items"] = this.dynamoToJSON(response_data["Items"]);

        return response_data;
    }

    synchronousScanContainsSourceId(sourceId, sortKey, startKey) {
        let body = `{"sourceId": "${sourceId}","sortKey": "${sortKey}"}`;

        if (startKey) {
            const lastSourceId = startKey.sourceId;
            const lastSortKey = startKey.sortKey;

            if (lastSourceId && lastSourceId.S && lastSortKey && lastSortKey.S) {
                body = `{"sourceId": "${sourceId}","sortKey": "${sortKey}", "lastSourceId": "${lastSourceId}", "lastSortKey": "${lastSortKey}"}`;
            }
        }

        let response_data = this._HttpService.async_result(`${this.baseUrl}/scan`, body, `POST`)
        response_data["Items"] = this.dynamoToJSON(response_data["Items"]);

        return response_data;
    }

    /**
     * Queries a single item given it's source id and sort key
     *
     * @param sourceId id
     * @param sortKey  filter/sort key
     * @return data
     */
    async queryBySourceId(sourceId, sortKey) {
        let response_data = await this._HttpService.async_result(`${this.baseUrl}/query`, `{"sourceId": "${sourceId}","sortKey": "${sortKey}"}`, `POST`)
        
        let test = this.dynamoToJSON(response_data);

        return response_data;
    }

    /**
     * Deletes an item from DB given keys
     *
     * @param sourceId sourceId key
     * @param sortKey sort key
     * @param alert alert function to call
     * @param refresh refresh function to call on success
     */
    deleteKeys(sourceId, sortKey, alert, refresh) {

        let url = `${this.baseUrl}/delete/${escape(sourceId)}/${escape(sortKey)}`;

        this._HttpService.async_result(url, null, 'delete')
        .then(() =>
            this._HttpService.onSuccess("Delete item", alert, refresh)
        ).catch(() =>
            this._HttpService.onFail('Delete item', alert)
        )
    }

    /**
     * Puts an item into DynamoDB
     *
     * @param item item to put
     * @param alert alert function to call
     * @param refresh refresh function to call on success
     */
    put(item, alert, refresh) {
        //add to fake avx
        this.put_into_avx(item, alert, refresh);

        let url = `${this.baseUrl}/put/`;

        let json = JSON.stringify(item);

        this._HttpService.asyncPost(url, json, "Put item", alert, refresh)
    }

    /**
     * Puts an item into the fake avx table if the source id is an ECN
     *
     * @param item item to put
     * @param alert alert function to call
     * @param refresh refresh function to call on success
     */
    put_into_avx(item, alert, refresh) {
        if (item.sourceId.startsWith("PATIENT_ECN")) {
            let ecn = item.sourceId.split('#')[1];
            let device = item.questions[0].source.deviceSerial;
            let event = {device_serial: device, ecn: ecn};

            let url = `${this.baseUrl}/add-to-avx/`;
            let json = JSON.stringify(event);

            this._HttpService.asyncPost(url, json, "Put item", alert, refresh)
        }
    }

    /**
     * Converts list of items from DynamoDB objects to JSON objects
     *
     * @param items list of items to convert
     * @return converted items
     */
    dynamoToJSON(items) {
        let converted = items;

        for (let key in converted) {
            converted[key] = this._AWSClient.DynamoDB.Converter.unmarshall(converted[key]);
        }

        return converted;
    }
}